import AppDescription from '@nxsys/components/AppDescription';
import AppLoader from '@nxsys/components/AppLoader';
import {
  defaultAepnChangeEventOptions,
  defaultAepnEnrolmentStatusOptions,
} from '@nxsys/constants/AppConstants';
import { useGetPensionByEmployeeId } from '@nxsys/hooks/api';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { Descriptions, Tabs, Typography } from 'antd';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import EmployeePrivatePensionTab from './components/EmployeePrivatePensionTab';

type Props = {
  employeeData: EmployeeRecordType;
  readOnly?: boolean;
};

const PensionIreland = ({ employeeData, readOnly }: Props) => {
  const { messages } = useTypedIntl();
  const [irelandTab, setIrelandTab] = useState('aepn');

  const {
    run: getPension,
    value: pensionValue,
    loading: isFetchingPension,
  } = useGetPensionByEmployeeId();
  const pensionData = useMemo(() => pensionValue?.data, [pensionValue]);

  useEffect(() => {
    getPension(employeeData.id);
  }, [employeeData]);

  const renderAepnContent = () => (
    <div className="p-[16px]">
      <AppDescription
        column={2}
        labelStyle={{ width: 216 }}
        bordered={false}
        colon={false}
        size="middle"
      >
        <Descriptions.Item label={messages['common.aepnNumber']}>
          {pensionData?.aepnNumber || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={messages['common.changeEvent']}>
          {defaultAepnChangeEventOptions.find(
            (option) => option.value === pensionData?.aepnChangeEvent,
          )?.label ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label={messages['common.enrolmentStatus']}>
          {defaultAepnEnrolmentStatusOptions.find(
            (option) => option.value === pensionData?.aepnEnrolmentStatus,
          )?.label ?? '-'}
        </Descriptions.Item>
        <Descriptions.Item label={messages['common.enrolmentDate']}>
          {pensionData?.aepnEnrolmentDate
            ? moment(pensionData.aepnEnrolmentDate)
                .tz('Europe/London')
                .format('DD/MM/YYYY')
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={messages['common.issueDate']}>
          {pensionData?.aepnIssueDate
            ? moment(pensionData.aepnIssueDate)
                .tz('Europe/London')
                .format('DD/MM/YYYY')
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={messages['common.downloadDate']}>
          {pensionData?.aepnDownloadDateTime
            ? moment(pensionData.aepnDownloadDateTime)
                .tz('Europe/London')
                .format('DD/MM/YYYY')
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={messages['common.employeeContributionRate']}>
          {pensionData?.eeContributionRate ?? '1.5%'}
        </Descriptions.Item>
        <Descriptions.Item label={messages['common.employerContributionRate']}>
          {pensionData?.erContributionRate ?? '1.5%'}
        </Descriptions.Item>
      </AppDescription>
    </div>
  );

  return (
    <div>
      {isFetchingPension && <AppLoader />}
      <Typography.Title
        level={5}
        className="!mb-6 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
      >
        {messages['common.pension']}
      </Typography.Title>
      <Tabs
        activeKey={irelandTab}
        onChange={setIrelandTab}
        items={[
          {
            key: 'aepn',
            label: 'My Future Fund',
            children: renderAepnContent(),
          },
          {
            key: 'privatePensions',
            label: messages['common.privatePensions'] ?? 'Private Pensions',
            children: (
              <div className="pt-4">
                <EmployeePrivatePensionTab
                  subcontractorId={employeeData.id}
                  isReadOnly={readOnly}
                />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default PensionIreland;
